import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  tableCellClasses,
  styled,
  Collapse,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Popover,
  Chip,
  Divider,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useMsal } from '@azure/msal-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'rgba(220, 20, 60, 0.08)',
    color: '#dc143c',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    color: '#3a3532',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
  '&:hover': { backgroundColor: 'rgba(220, 20, 60, 0.04)' },
  '& td': { borderColor: 'rgba(220, 20, 60, 0.1)' },
  '&.Mui-selected': {
    backgroundColor: 'rgba(220, 20, 60, 0.08)',
    '&:hover': { backgroundColor: 'rgba(220, 20, 60, 0.12)' },
  },
}));

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: '2px dashed rgba(220, 20, 60, 0.4)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#dc143c',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transform: 'translateY(-2px)',
  },
}));

// ─── Score helpers ────────────────────────────────────────────────────────────

const formatScore = (score) => {
  if (score === 'N/A') return 'N/A';
  const n = parseFloat(score);
  if (isNaN(n)) return 'N/A';
  return `${(n * 100).toFixed(1)}%`;
};

const getScoreColor = (score) => {
  if (score === 'N/A') return { bg: 'rgba(0,0,0,0.08)', text: '#666' };
  const n = parseFloat(score);
  if (isNaN(n)) return { bg: 'rgba(0,0,0,0.08)', text: '#666' };
  if (n >= 0.85) return { bg: 'rgba(46,125,50,0.2)',  text: '#2e7d32' };
  if (n >= 0.70) return { bg: 'rgba(46,125,50,0.1)',  text: '#2e7d32' };
  if (n >= 0.55) return { bg: 'rgba(237,108,2,0.15)', text: '#ed6c02' };
  if (n >= 0.40) return { bg: 'rgba(237,108,2,0.1)',  text: '#ed6c02' };
  return { bg: 'rgba(211,47,47,0.1)', text: '#d32f2f' };
};

const getScoreText = (score) => {
  if (score === 'N/A') return 'No score available';
  const n = parseFloat(score);
  if (isNaN(n)) return 'Invalid score';
  if (n >= 0.85) return 'Excellent match for role and skills';
  if (n >= 0.70) return 'Strong match with required qualifications';
  if (n >= 0.55) return 'Good match with some gaps';
  if (n >= 0.40) return 'Partial match or similar role';
  return 'Limited match or different role type';
};

// Bar fill color by value
const barColor = (v) => {
  if (v >= 0.75) return '#2e7d32';
  if (v >= 0.50) return '#ed6c02';
  return '#d32f2f';
};

const triggerDownload = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

// ─── Score Matrix Popover ─────────────────────────────────────────────────────

/**
 * Renders the Score Matrix button + popover for a single candidate.
 * Props:
 *   breakdown – the score_breakdown object from the API (or null)
 */
const ScoreMatrixCell = ({ breakdown }) => {
  const [anchor, setAnchor] = useState(null);

  if (!breakdown) {
    return (
      <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic' }}>
        N/A
      </Typography>
    );
  }

  const open = Boolean(anchor);

  const dimensions = [
    { label: 'Overall Match',   value: breakdown.overall_match,  tip: 'Semantic similarity between resume and JD text' },
    { label: 'Skills Match',    value: breakdown.skills_match,   tip: 'Fraction of JD keywords found in candidate skills' },
    { label: 'Experience Fit',  value: breakdown.experience_fit, tip: 'How well years of experience aligns with JD requirements' },
    { label: 'Role Relevance',  value: breakdown.role_relevance, tip: 'How relevant the candidate\'s role/domain is to the JD' },
  ];

  // Determine a summary label for the chip
  const avg = dimensions.reduce((s, d) => s + d.value, 0) / dimensions.length;
  const chipLabel = avg >= 0.75 ? 'Strong' : avg >= 0.50 ? 'Moderate' : 'Weak';
  const chipColor = avg >= 0.75 ? '#2e7d32' : avg >= 0.50 ? '#ed6c02' : '#d32f2f';
  const chipBg   = avg >= 0.75 ? 'rgba(46,125,50,0.12)' : avg >= 0.50 ? 'rgba(237,108,2,0.12)' : 'rgba(211,47,47,0.10)';

  return (
    <>
      {/* Trigger chip */}
      <Box
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 1.2,
          py: 0.4,
          borderRadius: '20px',
          bgcolor: chipBg,
          color: chipColor,
          fontWeight: 600,
          fontSize: '0.75rem',
          cursor: 'pointer',
          userSelect: 'none',
          border: `1px solid ${chipColor}30`,
          transition: 'all 0.18s',
          '&:hover': {
            bgcolor: chipColor + '22',
            boxShadow: `0 0 0 2px ${chipColor}30`,
          },
        }}
      >
        <BarChartIcon sx={{ fontSize: 14 }} />
        {chipLabel} · View
      </Box>

      {/* Popover panel */}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 6,
          sx: {
            width: 360,
            borderRadius: 2,
            border: '1px solid rgba(220,20,60,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5, py: 1.5,
            background: 'linear-gradient(135deg, rgba(220,20,60,0.06) 0%, rgba(220,20,60,0.02) 100%)',
            borderBottom: '1px solid rgba(220,20,60,0.10)',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#3a3532' }}>
            Score Matrix
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
            Breakdown of match dimensions
          </Typography>
        </Box>

        {/* Dimension bars */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {dimensions.map(({ label, value, tip }) => (
            <Tooltip title={tip} placement="right" arrow key={label}>
              <Box sx={{ mb: 1.6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#3a3532' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: barColor(value) }}>
                    {(value * 100).toFixed(0)}%
                  </Typography>
                </Box>
                {/* Track */}
                <Box
                  sx={{
                    height: 7,
                    borderRadius: 4,
                    bgcolor: 'rgba(0,0,0,0.07)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Fill */}
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.round(value * 100)}%`,
                      borderRadius: 4,
                      bgcolor: barColor(value),
                      transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(220,20,60,0.08)' }} />

        {/* Matched / Missing skills */}
        {(breakdown.matched_skills?.length > 0 || breakdown.missing_skills?.length > 0) && (
          <Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
            {breakdown.matched_skills?.length > 0 && (
              <Box sx={{ mb: 1.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#2e7d32' }}>
                    Matched Skills
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {breakdown.matched_skills.slice(0, 8).map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        bgcolor: 'rgba(46,125,50,0.10)',
                        color: '#2e7d32',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {breakdown.missing_skills?.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                  <CancelOutlinedIcon sx={{ fontSize: 13, color: '#d32f2f' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#d32f2f' }}>
                    Possible Gaps
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {breakdown.missing_skills.slice(0, 6).map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        bgcolor: 'rgba(211,47,47,0.08)',
                        color: '#d32f2f',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(220,20,60,0.08)' }} />

        {/* Reason for score */}
        <Box sx={{ px: 2.5, pt: 1.4, pb: 2 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Reason for Score
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#3a3532', lineHeight: 1.55 }}>
            {breakdown.reason || 'No rationale available.'}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

// ─── Skill / Education cells (unchanged) ─────────────────────────────────────

const SkillCell = ({ text, title = 'Skills' }) => {
  const [open, setOpen] = useState(false);
  const arr = text ? text.split(',').map((s) => s.trim()) : [];
  const preview = arr.slice(0, 3).join(', ');

  return (
    <Box sx={{ maxWidth: 250, whiteSpace: 'pre-wrap' }}>
      {preview}
      {arr.length > 3 && (
        <>
          <Typography
            component="span"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            sx={{ color: '#1976d2', fontSize: '0.875rem', ml: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            ...Show more
          </Typography>
          <Dialog open={open} onClose={(e) => { e.stopPropagation(); setOpen(false); }} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: '#3a3532', fontWeight: 600 }}>{title}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>Complete list of {title.toLowerCase()}:</Typography>
              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1, maxHeight: 250, overflowY: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#3a3532' }}>{arr.join(', ')}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }} sx={{ color: '#dc143c' }}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const EducationCell = ({ text, title = 'Education' }) => {
  const [open, setOpen] = useState(false);
  if (!text) return 'N/A';
  const maxLen = 60;
  const isLong = text.length > maxLen;
  const preview = isLong ? `${text.substring(0, maxLen)}...` : text;

  return (
    <Box sx={{ maxWidth: 250, whiteSpace: 'normal' }}>
      <Typography variant="body2" sx={{ display: 'inline', color: '#3a3532', fontSize: '0.875rem' }}>{preview}</Typography>
      {isLong && (
        <>
          <Typography
            component="span"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            sx={{ color: '#1976d2', fontSize: '0.875rem', ml: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Show more
          </Typography>
          <Dialog open={open} onClose={(e) => { e.stopPropagation(); setOpen(false); }} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: '#3a3532', fontWeight: 600 }}>{title}</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1, maxHeight: 250, overflowY: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#3a3532', whiteSpace: 'pre-wrap' }}>{text}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }} sx={{ color: '#dc143c' }}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UploadWizard = () => {
  const { accounts } = useMsal();
  const [resumes, setResumes] = useState([]);
  const [jd, setJd] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUpload, setShowUpload] = useState(true);
  const [showResumeList, setShowResumeList] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [anchorElExportSelected, setAnchorElExportSelected] = useState(null);
  const [anchorElExportAll, setAnchorElExportAll] = useState(null);
  const [candidateErrors, setCandidateErrors] = useState([]);
  const [pipelineWarnings, setPipelineWarnings] = useState([]);

  const { getRootProps: getResumeRootProps, getInputProps: getResumeInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: (acceptedFiles) => {
      setResumes((prev) => [...prev, ...acceptedFiles]);
      setShowResumeList(true);
      setError(null);
    },
  });

  const { getRootProps: getJdRootProps, getInputProps: getJdInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => { setJd(acceptedFiles[0]); setError(null); },
  });

  const handleRemoveResume = (idx) => {
    setResumes((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length === 0) setShowResumeList(false);
      return updated;
    });
  };

  const handleUpload = async () => {
    setLoading(true);
    setError(null);
    setCandidateErrors([]);
    setPipelineWarnings([]);
    setUploadProgress(0);
    const activeUser = accounts.length > 0 ? accounts[0].username : 'Anonymous';

    try {
      const resumeForm = new FormData();
      resumes.forEach((f) => resumeForm.append('file', f));
      const resumeRes = await fetch(`${API_BASE_URL}/uploadpdf`, {
        method: 'POST', body: resumeForm, credentials: 'include',
        headers: { 'user-id': activeUser, 'X-User-Email': activeUser },
      });
      if (!resumeRes.ok) throw new Error('Failed to upload resumes');
      setUploadProgress(50);

      if (jd) {
        const jdForm = new FormData();
        jdForm.append('file', jd);
        const jdRes = await fetch(`${API_BASE_URL}/uploadjd`, {
          method: 'POST', body: jdForm, credentials: 'include',
          headers: { 'user-id': activeUser, 'X-User-Email': activeUser },
        });
        if (!jdRes.ok) throw new Error('Failed to upload JD');
      }
      setUploadProgress(75);

      const processRes = await fetch(`${API_BASE_URL}/extract`, {
        method: 'POST', credentials: 'include',
        headers: { 'user-id': activeUser, 'X-User-Email': activeUser },
      });
      if (!processRes.ok) throw new Error('Failed to process files');

      const data = await processRes.json();
      // API returns { results: [...], warnings: [...], errors: [...] }
      const rows = Array.isArray(data) ? data : (data.results || []);
      setResults(rows);
      setCandidateErrors(data.errors || []);
      setPipelineWarnings(data.warnings || []);
      setSelectedProfiles([]);
      setShowUpload(false);
      setUploadProgress(100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearFiles = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/clear`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to clear files');
        return;
      }
      setResumes([]); setJd(null); setResults([]); setSelectedProfiles([]);
      setError(null); setCandidateErrors([]); setPipelineWarnings([]);
      setUploadProgress(0); setShowUpload(true); setShowResumeList(false);
    } catch (err) {
      setError('Failed to clear files: ' + err.message);
    }
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) { setSelectedProfiles(results.map((_, i) => i)); return; }
    setSelectedProfiles([]);
  };

  const handleClick = (e, index) => {
    e.stopPropagation();
    const sel = selectedProfiles.indexOf(index);
    let next = [];
    if (sel === -1) next = [...selectedProfiles, index];
    else next = selectedProfiles.filter((i) => i !== index);
    setSelectedProfiles(next);
  };

  const isSelected = (index) => selectedProfiles.includes(index);

  const getExportFilename = (type, ext = 'pdf') => {
    let base = 'Candidates';
    if (jd?.name) base = jd.name.replace(/\.[^/.]+$/, '').replace(/\s+/g, '_');
    return `ResumeX_${base}_${type}.${ext}`;
  };

  // ── Export helpers ──────────────────────────────────────────────────────────

  const generatePDF = (data, fileName) => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(20); doc.setTextColor(220, 20, 60);
    doc.text('ResumeX Candidate Matching Report', 14, 22);
    autoTable(doc, {
      startY: 35,
      head: [['Candidate Name', 'Email', 'Job Role', 'Education', 'Skills', 'Experience', 'Match Score', 'Score Summary']],
      body: data.map((r) => {
        const bd = r.score_breakdown;
        const summary = bd
          ? `Overall:${(bd.overall_match*100).toFixed(0)}% Skills:${(bd.skills_match*100).toFixed(0)}% Exp:${(bd.experience_fit*100).toFixed(0)}% Role:${(bd.role_relevance*100).toFixed(0)}%`
          : 'N/A';
        return [
          r['Candidate Name'] || 'N/A', r['Email'] || 'N/A', r['Job Role'] || 'N/A',
          r['Education'] || 'N/A', r['Skills'] || 'N/A',
          r['Years of Experience'] || 'N/A', formatScore(r.similarity_score), summary,
        ];
      }),
      theme: 'grid',
      headStyles: { fillColor: [220, 20, 60] },
      styles: { fontSize: 8 },
    });
    doc.save(fileName);
  };

  const generateExcel = (data, fileName) => {
    const rows = data.map((r) => {
      const bd = r.score_breakdown;
      return {
        'Candidate Name': r['Candidate Name'] || 'N/A',
        'Email': r['Email'] || 'N/A',
        'Job Role': r['Job Role'] || 'N/A',
        'Education': r['Education'] || 'N/A',
        'Skills': r['Skills'] || 'N/A',
        'Experience': r['Years of Experience'] || 'N/A',
        'Match Score': formatScore(r.similarity_score),
        'Overall Match %': bd ? `${(bd.overall_match*100).toFixed(0)}%` : 'N/A',
        'Skills Match %': bd ? `${(bd.skills_match*100).toFixed(0)}%` : 'N/A',
        'Experience Fit %': bd ? `${(bd.experience_fit*100).toFixed(0)}%` : 'N/A',
        'Role Relevance %': bd ? `${(bd.role_relevance*100).toFixed(0)}%` : 'N/A',
        'Reason for Score': bd?.reason || 'N/A',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [25,30,25,40,50,15,15,18,18,18,18,60].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
    XLSX.writeFile(wb, fileName);
  };

  const generateCSV = (data, fileName) => {
    const headers = ['Candidate Name','Email','Job Role','Education','Skills','Experience','Match Score','Overall Match %','Skills Match %','Experience Fit %','Role Relevance %','Reason for Score'];
    const rows = [headers.join(',')];
    data.forEach((r) => {
      const bd = r.score_breakdown;
      const esc = (v) => `"${String(v || 'N/A').replace(/"/g, '""')}"`;
      rows.push([
        esc(r['Candidate Name']), esc(r['Email']), esc(r['Job Role']),
        esc(r['Education']), esc(r['Skills']), esc(r['Years of Experience']),
        esc(formatScore(r.similarity_score)),
        esc(bd ? `${(bd.overall_match*100).toFixed(0)}%` : 'N/A'),
        esc(bd ? `${(bd.skills_match*100).toFixed(0)}%` : 'N/A'),
        esc(bd ? `${(bd.experience_fit*100).toFixed(0)}%` : 'N/A'),
        esc(bd ? `${(bd.role_relevance*100).toFixed(0)}%` : 'N/A'),
        esc(bd?.reason),
      ].join(','));
    });
    triggerDownload(rows.join('\n'), fileName, 'text/csv;charset=utf-8;');
  };

  const handleExport = (format, isSelectedOnly) => {
    const data = isSelectedOnly ? results.filter((_, i) => selectedProfiles.includes(i)) : results;
    const ext = format === 'excel' ? 'xlsx' : format;
    const suffix = isSelectedOnly ? 'Selected' : 'All';
    const fileName = getExportFilename(suffix, ext);
    if (format === 'pdf')   generatePDF(data, fileName);
    if (format === 'csv')   generateCSV(data, fileName);
    if (format === 'excel') generateExcel(data, fileName);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar hasResults={results.length > 0} onClearResults={clearFiles} />
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        <Collapse in={showUpload}>
          <Paper elevation={0} sx={{
            p: 4, mb: 4,
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            border: '1px solid rgba(220,20,60,0.1)',
            boxShadow: '0 8px 32px rgba(220,20,60,0.1)',
          }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#3a3532' }}>
              Upload Files
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              {/* Resume dropzone */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Resumes (PDF)
                </Typography>
                <DropzoneBox {...getResumeRootProps()}>
                  <input {...getResumeInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {resumes.length > 0 ? 'Drag & drop more resumes or click to add' : 'Drag & drop resumes here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>

                {resumes.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button fullWidth onClick={() => setShowResumeList(!showResumeList)}
                      endIcon={showResumeList ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      sx={{ justifyContent: 'space-between', color: '#3a3532', backgroundColor: 'rgba(220,20,60,0.04)', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: 'rgba(220,20,60,0.08)' } }}
                    >
                      Show Uploaded Resumes ({resumes.length})
                    </Button>
                    <Collapse in={showResumeList}>
                      <List sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid rgba(220,20,60,0.1)', borderRadius: 1, mt: 1, bgcolor: 'rgba(255,255,255,0.6)' }}>
                        {resumes.map((file, index) => (
                          <ListItem key={index}
                            secondaryAction={
                              <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleRemoveResume(index); }}
                                sx={{ color: '#dc143c', '&:hover': { bgcolor: 'rgba(220,20,60,0.1)' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            }
                            sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}
                          >
                            <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`}
                              primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { maxWidth: '80%', color: '#3a3532', fontWeight: 500 } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                )}
              </Box>

              {/* JD dropzone */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Job Description (PDF)
                </Typography>
                <DropzoneBox {...getJdRootProps()}>
                  <input {...getJdInputProps()} />
                  <UploadFileIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {jd ? jd.name : 'Drag & drop JD here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button variant="contained" onClick={handleUpload} disabled={resumes.length === 0 || !jd || loading}
                startIcon={<AnalyticsIcon />}
                sx={{ backgroundColor: '#dc143c', '&:hover': { backgroundColor: '#b01030' } }}>
                Analyze Files
              </Button>
              <Button variant="outlined" onClick={clearFiles} disabled={loading} startIcon={<DeleteIcon />}
                sx={{ color: '#dc143c', borderColor: '#dc143c', '&:hover': { borderColor: '#dc143c', backgroundColor: 'rgba(220,20,60,0.04)' } }}>
                Clear Files
              </Button>
            </Box>

            {loading && (
              <Box sx={{ width: '100%', mb: 3 }}>
                <LinearProgress variant="determinate" value={uploadProgress}
                  sx={{ backgroundColor: 'rgba(220,20,60,0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#dc143c' } }} />
                <Typography variant="body2" sx={{ mt: 1, color: '#3a3532' }}>{uploadProgress}% Complete</Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3, '& .MuiAlert-icon': { color: '#dc143c' } }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Collapse>

        {/* Candidate-data errors — no email detected / duplicate email-phone.
            These need recruiter attention, so they stay visible regardless of
            whether the upload panel is collapsed. */}
        {candidateErrors.length > 0 && (
          <Alert
            severity="error"
            onClose={() => setCandidateErrors([])}
            sx={{ mb: 2, '& .MuiAlert-icon': { color: '#d32f2f' } }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
              {candidateErrors.length === 1
                ? '1 resume could not be added:'
                : `${candidateErrors.length} resumes could not be added:`}
            </Typography>
            <List dense disablePadding>
              {candidateErrors.map((msg, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.2 }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                    primary={`• ${msg}`}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Softer informational warnings — JD detected in resume slot, or a
            resume that failed extraction outright. */}
        {pipelineWarnings.length > 0 && (
          <Alert
            severity="warning"
            onClose={() => setPipelineWarnings([])}
            sx={{ mb: 2 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
              {pipelineWarnings.length === 1 ? 'Note:' : `${pipelineWarnings.length} notes:`}
            </Typography>
            <List dense disablePadding>
              {pipelineWarnings.map((msg, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.2 }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                    primary={`• ${msg}`}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Results table */}
        {results.length > 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ color: '#3a3532', fontWeight: 600 }}>
                Analysis Results
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {selectedProfiles.length > 0 && (
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                    {selectedProfiles.length} selected
                  </Typography>
                )}

                {/* Export Selected */}
                <Button variant="outlined" startIcon={<DownloadIcon />}
                  disabled={selectedProfiles.length === 0}
                  onClick={(e) => setAnchorElExportSelected(e.currentTarget)}
                  sx={{ color: '#dc143c', borderColor: '#dc143c', '&:hover': { borderColor: '#dc143c', backgroundColor: 'rgba(220,20,60,0.04)' }, '&.Mui-disabled': { borderColor: 'rgba(0,0,0,0.12)' } }}>
                  Export Selected
                </Button>
                <Menu anchorEl={anchorElExportSelected} open={Boolean(anchorElExportSelected)} onClose={() => setAnchorElExportSelected(null)}>
                  <MenuItem onClick={() => { handleExport('pdf', true); setAnchorElExportSelected(null); }}>Export as PDF</MenuItem>
                  <MenuItem onClick={() => { handleExport('excel', true); setAnchorElExportSelected(null); }}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => { handleExport('csv', true); setAnchorElExportSelected(null); }}>Export as CSV</MenuItem>
                </Menu>

                {/* Export All */}
                <Button variant="contained" startIcon={<DownloadIcon />}
                  onClick={(e) => setAnchorElExportAll(e.currentTarget)}
                  sx={{ backgroundColor: '#dc143c', '&:hover': { backgroundColor: '#b01030' } }}>
                  Export All
                </Button>
                <Menu anchorEl={anchorElExportAll} open={Boolean(anchorElExportAll)} onClose={() => setAnchorElExportAll(null)}>
                  <MenuItem onClick={() => { handleExport('pdf', false); setAnchorElExportAll(null); }}>Export as PDF</MenuItem>
                  <MenuItem onClick={() => { handleExport('excel', false); setAnchorElExportAll(null); }}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => { handleExport('csv', false); setAnchorElExportAll(null); }}>Export as CSV</MenuItem>
                </Menu>

                <Button startIcon={showUpload ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => setShowUpload(!showUpload)} sx={{ color: '#dc143c', ml: 1 }}>
                  {showUpload ? 'Hide Upload' : 'Show Upload'}
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              border: '1px solid rgba(220,20,60,0.1)',
              boxShadow: '0 8px 32px rgba(220,20,60,0.1)',
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        sx={{ color: 'rgba(220,20,60,0.5)', '&.Mui-checked': { color: '#dc143c' }, '&.MuiCheckbox-indeterminate': { color: '#dc143c' } }}
                        indeterminate={selectedProfiles.length > 0 && selectedProfiles.length < results.length}
                        checked={results.length > 0 && selectedProfiles.length === results.length}
                        onChange={handleSelectAllClick}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 160 }}>Candidate Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Job Role</StyledTableCell>
                    <StyledTableCell>Education</StyledTableCell>
                    <StyledTableCell>Skills</StyledTableCell>
                    <StyledTableCell>Experience</StyledTableCell>
                    <StyledTableCell>
                      Match Score
                      <Tooltip title="Calculated based on role match, skills, and experience">
                        <HelpOutlineIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle', opacity: 0.7 }} />
                      </Tooltip>
                    </StyledTableCell>

                    {/* ── NEW COLUMN ── */}
                    <StyledTableCell sx={{ minWidth: 140 }}>
                      Score Matrix
                      <Tooltip title="Breakdown of match score across 4 dimensions + reason for score. Click 'View' on any row to expand.">
                        <HelpOutlineIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle', opacity: 0.7 }} />
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {results.map((row, index) => {
                    const selected = isSelected(index);
                    return (
                      <StyledTableRow
                        key={index}
                        onClick={(e) => handleClick(e, index)}
                        selected={selected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <StyledTableCell padding="checkbox">
                          <Checkbox
                            sx={{ color: 'rgba(220,20,60,0.5)', '&.Mui-checked': { color: '#dc143c' } }}
                            checked={selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleClick(e, index)}
                          />
                        </StyledTableCell>
                        <StyledTableCell>{row['Candidate Name'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>{row['Email'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>{row['Job Role'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>
                          {row['Education'] ? <EducationCell text={row['Education']} /> : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {row['Skills'] ? <SkillCell text={row['Skills']} /> : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell>{row['Years of Experience'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title={getScoreText(row.similarity_score)}>
                            <Box sx={{
                              minWidth: 60, height: 24, px: 1,
                              bgcolor: getScoreColor(row.similarity_score).bg,
                              color: getScoreColor(row.similarity_score).text,
                              borderRadius: 1,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.875rem', fontWeight: 600,
                            }}>
                              {formatScore(row.similarity_score)}
                            </Box>
                          </Tooltip>
                        </StyledTableCell>

                        {/* ── Score Matrix cell ── */}
                        <StyledTableCell onClick={(e) => e.stopPropagation()}>
                          <ScoreMatrixCell breakdown={row.score_breakdown} />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
      <Chatbot />
    </Box>
  );
};

export default UploadWizard;